import { useState, useEffect } from 'react';
import { ChevronDown, FileText } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { EstimatorSelector } from '../components/EstimatorSelector';
import { AccountSummaryCard } from '../components/AccountSummaryCard';
import { TransactionFilters } from '../components/TransactionFilters';
import { CommissionTransactionList } from '../components/CommissionTransactionList';
import { RecordDrawModal } from '../components/RecordDrawModal';
import { ReconcileDateModal } from '../components/ReconcileDateModal';
import { AuditHistoryViewer } from '../components/AuditHistoryViewer';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useEstimatorAccount } from '../hooks/useEstimatorAccount';
import { useCommissionTransactions } from '../hooks/useCommissionTransactions';
import { useEstimatorReport } from '../hooks/useEstimatorReport';
import { usePageTitle } from '@/hooks/usePageTitle';
import type { TransactionFilters as FilterType } from '../types/commission';

/**
 * Estimator Accounts page for viewing commissions and recording draws
 * - Admins can view any estimator's account and record draws
 * - Estimators can only view their own account
 */
export function EstimatorAccountsPage() {
  usePageTitle('Estimator Accounts');
  const { user } = useAuth();
  const isAdmin = user?.roles?.includes('Admin') ?? false;

  // Auto-select current user if not admin (initialize with user ID)
  const initialUserId = !isAdmin && user?.id ? user.id : null;
  const [selectedUserId, setSelectedUserId] = useState<number | null>(initialUserId);
  const [filters, setFilters] = useState<FilterType>({});
  const [isDrawModalOpen, setIsDrawModalOpen] = useState(false);
  const [isReconcileModalOpen, setIsReconcileModalOpen] = useState(false);

  // Update selectedUserId when user changes (for non-admins only)
  useEffect(() => {
    if (!isAdmin && user?.id && selectedUserId !== user.id) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedUserId(user.id);
    }
  }, [isAdmin, user, selectedUserId]);

  const { downloadPdf, downloadExcel, openPdf, isLoading: reportLoading } =
    useEstimatorReport(selectedUserId);

  const { data: account, isLoading: accountLoading, refetch: refetchAccount } =
    useEstimatorAccount(selectedUserId);

  const { data: transactions, isLoading: transactionsLoading, refetch: refetchTransactions } =
    useCommissionTransactions(selectedUserId, filters);

  const handleDrawSuccess = () => {
    setIsDrawModalOpen(false);
    refetchAccount();
    refetchTransactions();
  };

  const handleReconcileSuccess = () => {
    setIsReconcileModalOpen(false);
    refetchAccount();
    refetchTransactions();
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold">Estimator Accounts</h1>
        {selectedUserId && (
          <div className="flex gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  disabled={reportLoading}
                >
                  <FileText className="h-4 w-4 mr-1" />
                  {reportLoading ? 'Loading...' : 'Report'}
                  <ChevronDown className="h-3 w-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white">
                <DropdownMenuItem
                  onClick={() => openPdf({ startDate: filters.startDate, endDate: filters.endDate })}
                  className="cursor-pointer"
                >
                  View PDF
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => downloadPdf({ startDate: filters.startDate, endDate: filters.endDate })}
                  className="cursor-pointer"
                >
                  Download PDF
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => downloadExcel({ startDate: filters.startDate, endDate: filters.endDate })}
                  className="cursor-pointer"
                >
                  Download Excel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {isAdmin && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setIsReconcileModalOpen(true)}
                >
                  Set Reconcile Date
                </Button>
                <Button
                  onClick={() => setIsDrawModalOpen(true)}
                  className="bg-secondary text-white hover:bg-secondary/90"
                >
                  Record Draw
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {isAdmin && (
        <EstimatorSelector
          selectedUserId={selectedUserId}
          onSelect={setSelectedUserId}
        />
      )}

      {selectedUserId ? (
        <>
          <AccountSummaryCard
            account={account}
            isLoading={accountLoading}
          />

          <TransactionFilters
            filters={filters}
            onChange={setFilters}
          />

          <CommissionTransactionList
            transactionList={transactions}
            isLoading={transactionsLoading}
          />

          <AuditHistoryViewer userId={selectedUserId} />
        </>
      ) : (
        <div className="text-center py-12 text-gray-500">
          Select an estimator to view their account
        </div>
      )}

      {isAdmin && (
        <>
          <RecordDrawModal
            isOpen={isDrawModalOpen}
            onClose={() => setIsDrawModalOpen(false)}
            onSuccess={handleDrawSuccess}
            userId={selectedUserId}
          />
          <ReconcileDateModal
            isOpen={isReconcileModalOpen}
            onClose={() => setIsReconcileModalOpen(false)}
            onSuccess={handleReconcileSuccess}
            userId={selectedUserId}
            userName={account?.userName ?? ''}
          />
        </>
      )}
    </div>
  );
}
